@file:OptIn(ExperimentalComposeWebSvgApi::class)

package com.algorix.web

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.ExperimentalComposeWebSvgApi
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.svg.Path
import org.jetbrains.compose.web.svg.Svg
import org.jetbrains.compose.web.svg.Use
import org.w3c.dom.HTMLElement

private val figureBuilder = ElementBuilder.createBuilder<HTMLElement>("figure")
private val figcaptionBuilder = ElementBuilder.createBuilder<HTMLElement>("figcaption")

@Composable
fun Figure(attrs: (AttrsScope<HTMLElement>.() -> Unit)? = null, content: @Composable ElementScope<HTMLElement>.() -> Unit) =
    TagElement(figureBuilder, attrs, content)

@Composable
fun Figcaption(attrs: (AttrsScope<HTMLElement>.() -> Unit)? = null, content: @Composable ElementScope<HTMLElement>.() -> Unit) =
    TagElement(figcaptionBuilder, attrs, content)

fun AttrsScope<*>.aria(name: String, value: String) = attr("aria-$name", value)

/** Image whose source follows the language when a Turkish screenshot exists. */
fun localized(en: String, tr: String?): String = if (I18n.lang == Lang.TR && tr != null) tr else en

/** A phone frame (iPhone or Android) around one screenshot. */
@Composable
fun Device(
    ios: Boolean,
    srcEn: String,
    srcTr: String?,
    altKey: String,
    lazy: Boolean = true,
    priority: Boolean = false,
    fog: Boolean = false,
) {
    Div({ classes("dev", if (ios) "ios" else "and") }) {
        Div({ classes("scr") }) {
            Img(src = localized(srcEn, srcTr), alt = t(altKey)) {
                attr("width", "640")
                attr("height", if (ios) "1391" else "1280")
                attr("decoding", "async")
                if (lazy) attr("loading", "lazy")
                if (priority) attr("fetchpriority", "high")
            }
        }
        if (fog) Span({ classes("fog") })
    }
}

@Composable
fun StoreIcon(id: String, cls: String) {
    Svg(attrs = {
        classes(cls)
        aria("hidden", "true")
        attr("focusable", "false")
    }) { Use(href = "#$id") }
}

@Composable
fun SrNewTab() = Span({ classes("sr-only") }) { Text(t("newtab")) }

@Composable
fun ExternalLink(href: String, attrs: AttrsScope<*>.() -> Unit = {}, content: @Composable () -> Unit) {
    A(href = href, attrs = {
        attrs()
        target(ATarget.Blank)
        attr("rel", "noopener")
    }) {
        content()
        SrNewTab()
    }
}

enum class Store(val icon: String, val label: String) { APP_STORE("i-as", "App Store"), GOOGLE_PLAY("i-gp", "Google Play") }

@Composable
fun StoreLive(store: Store, href: String) {
    ExternalLink(href, { classes("store", "is-live") }) {
        StoreIcon(store.icon, "s-ico")
        Span({ classes("s-txt") }) {
            Span({ classes("s-sub") }) { Tx("store.avail") }
            Span({ classes("s-name") }) { Text(store.label) }
        }
    }
}

@Composable
fun StoreSoon(store: Store) {
    Span({ classes("store", "is-soon") }) {
        StoreIcon(store.icon, "s-ico")
        Span({ classes("s-txt") }) {
            Span({ classes("s-sub") }) { Tx("store.soon") }
            Span({ classes("s-name") }) { Text(store.label) }
        }
    }
}

/** Stroked 16/18 px glyph used on buttons. */
@Composable
fun Glyph(viewBox: String, d: String, cls: String? = null, filled: Boolean = false, round: Boolean = true) {
    Svg(viewBox = viewBox, attrs = {
        if (cls != null) classes(cls)
        aria("hidden", "true")
        attr("focusable", "false")
    }) {
        Path(d = d, attrs = {
            if (filled) {
                attr("fill", "currentColor")
            } else {
                attr("fill", "none")
                attr("stroke", "currentColor")
                attr("stroke-width", if (viewBox.endsWith("16 16")) "1.6" else "1.7")
                attr("stroke-linecap", "round")
                if (round) attr("stroke-linejoin", "round")
            }
        })
    }
}
